import { useState, useEffect, useCallback } from 'react';

interface ModalState {
  isDetalleModalOpen: boolean;
  selectedObligacionId: string | null;
  activeTab: string;
}

const MODAL_STATE_KEY = 'viabiliza_modal_state';

export const useModalPersistence = () => {
  const [modalState, setModalState] = useState<ModalState>({
    isDetalleModalOpen: false,
    selectedObligacionId: null,
    activeTab: 'detalles'
  });

  // Load modal state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem(MODAL_STATE_KEY);
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        setModalState(parsedState);
      } catch (error) {
        console.warn('Error parsing saved modal state:', error);
      }
    }
  }, []);

  // Save modal state when it changes
  const saveModalState = useCallback((newState: Partial<ModalState>) => {
    const updatedState = { ...modalState, ...newState };
    setModalState(updatedState);
    localStorage.setItem(MODAL_STATE_KEY, JSON.stringify(updatedState));
  }, [modalState]);

  // Save state when page is hidden or before unload
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        localStorage.setItem(MODAL_STATE_KEY, JSON.stringify(modalState));
      }
    };

    const handleBeforeUnload = () => {
      localStorage.setItem(MODAL_STATE_KEY, JSON.stringify(modalState));
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [modalState]);

  // Restore modal state when app regains focus
  useEffect(() => {
    const handleFocus = () => {
      const savedState = localStorage.getItem(MODAL_STATE_KEY);
      if (savedState) {
        try {
          const parsedState = JSON.parse(savedState);
          if (parsedState.isDetalleModalOpen) {
            setModalState(parsedState);
          }
        } catch (error) {
          console.warn('Error restoring modal state:', error);
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleFocus();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const openDetalleModal = useCallback((obligacionId: string, tab: string = 'detalles') => {
    saveModalState({
      isDetalleModalOpen: true,
      selectedObligacionId: obligacionId,
      activeTab: tab
    });
  }, [saveModalState]);

  const closeDetalleModal = useCallback(() => {
    saveModalState({
      isDetalleModalOpen: false,
      selectedObligacionId: null,
      activeTab: 'detalles'
    });
  }, [saveModalState]);

  const setActiveTab = useCallback((tab: string) => {
    saveModalState({ activeTab: tab });
  }, [saveModalState]);

  const clearModalState = useCallback(() => {
    localStorage.removeItem(MODAL_STATE_KEY);
    setModalState({
      isDetalleModalOpen: false,
      selectedObligacionId: null,
      activeTab: 'detalles'
    });
  }, []);

  return {
    modalState,
    openDetalleModal,
    closeDetalleModal,
    setActiveTab,
    clearModalState
  };
};